import type Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { JournalGroupAddUserItem } from 'donor-elf-web/components/journal-group/add-users';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import JournalGroupUserModel from 'donor-elf-web/models/all-funds/journal-group-user';
import AllFundsJournalSlimModel from 'donor-elf-web/models/all-funds/journal-slim';
import { JournalGroupEditRouteModel } from 'donor-elf-web/routes/funds/all/journal-groups/journal-group/edit';
import CurrentUser from 'donor-elf-web/services/current-user';

export default class JournalGroupEditController extends Controller {
  @service declare currentUser: CurrentUser;
  @service declare router: Router;
  @service declare store: Store;

  @tracked model!: JournalGroupEditRouteModel;

  @tracked saving = false;
  @tracked deleting = false;
  @tracked showValidationErrors = false;
  @tracked error?: any;
  @tracked editJournals = false;
  @tracked addUser = false;

  @action
  templateWillDestroy() {
    this.saving = false;
    this.deleting = false;
    this.showValidationErrors = false;
    this.editJournals = false;
    this.addUser = false;
  }

  get isNewRecord() {
    return this.model.journalGroup.isNew;
  }

  @cached
  get userCanEditJournalGroup() {
    return this.model.journalGroupUsers
      .filter((x) => x.isOwner)
      .some((x) => x.userId === this.currentUser.id);
  }

  @cached
  get sortedJournalGroupUsers() {
    return sortByProperties(this.model.journalGroupUsers, [
      'isOwner:desc',
      'user.lastName:asc',
      'user.firstName:asc',
    ]);
  }

  @action
  handleEditJournals() {
    this.editJournals = true;
  }

  @action
  handleSaveEditJournals(slimJournals: AllFundsJournalSlimModel[]) {
    this.model.slimJournals = slimJournals;
    this.editJournals = false;
  }

  @action
  handleCancelEditJournals() {
    this.editJournals = false;
  }

  @action
  handleAddUser() {
    this.addUser = true;
  }

  @action
  async handleSaveAddUsers(addedUserItems: JournalGroupAddUserItem[]) {
    this.model.journalGroupUsers = [
      ...this.model.journalGroupUsers,
      ...addedUserItems.map((item) => {
        return this.store.createRecord('all-funds/journal-group-user', {
          journalGroupId: this.model.journalGroup.id,
          userId: item.user.id,
          isOwner: item.isOwner,
          user: item.user,
        });
      }),
    ];
    this.saveUsersToChangeset();
    await this.model.changeset.validate();
    this.addUser = false;
  }

  @action
  handleCancelAddUsers() {
    this.addUser = false;
  }

  @action
  async handleToggleIsOwnerJournalGroupUser(
    journalGroupUser: JournalGroupUserModel,
    dropdown: any,
  ) {
    journalGroupUser.isOwner = !journalGroupUser.isOwner;
    this.saveUsersToChangeset();
    await this.model.changeset.validate();
    dropdown.actions.close();
  }

  @action
  async handleDeleteJournalGroupUser(journalGroupUser: JournalGroupUserModel) {
    this.model.journalGroupUsers = this.model.journalGroupUsers.filter(
      (x) => x.userId !== journalGroupUser.userId,
    );
    this.saveUsersToChangeset();
    await this.model.changeset.validate();
  }

  saveUsersToChangeset() {
    this.model.changeset.set(
      'users',
      this.model.journalGroupUsers.map((x) => {
        return { id: x.userId, is_owner: x.isOwner };
      }),
    );
  }

  @action
  async save() {
    this.saving = true;
    let { changeset } = this.model;
    changeset.set(
      'journalIds',
      this.model.slimJournals.map((x) => x.id),
    );
    this.saveUsersToChangeset();
    await changeset.validate();
    if (changeset.get('isValid')) {
      try {
        await changeset.save();
        this.router.transitionTo(
          'funds.all.journal-groups.journal-group',
          this.model.journalGroup.id,
        );
      } catch (error) {
        this.saving = false;
        this.error = error;
      }
    } else {
      this.showValidationErrors = true;
    }
    this.saving = false;
  }

  @action
  cancel() {
    window.history.back();
  }

  @action
  async delete() {
    this.deleting = true;
    await this.model.journalGroup.destroyRecord();
    this.router.transitionTo('funds.all');
  }
}
